import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Luggage, RefreshCw, Search, Filter, Loader2, Volume2, Monitor, ChevronRight,
  PlaneLanding, Clock3, Zap, PackageCheck, Radio, ListChecks, UserCircle2, UserCog, X, Check, Globe,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { beltAssignmentsAPI, announcementZonesAPI, adminAPI } from '../../services/api';
import PageHeader from '../../components/UI/PageHeader';
import EmptyState from '../../components/UI/EmptyState';
import StatusBadge from '../../components/UI/StatusBadge';
import Button from '../../components/UI/Button';
import { languagePairLabel } from '../../utils/languageLabel';

function timeAgo(iso) {
  if (!iso) return null;
  const s = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (s < 0) return 'just now';
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m ago`;
}

function timeOfDay(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// Derived operational status — more useful for baggage ops than the underlying
// active/completed/cancelled lifecycle column, which barely changes hour to hour.
const STATUS_CONFIG = {
  awaiting:  { key: 'awaiting',  label: 'Awaiting Arrival',       tone: 'neutral' },
  pending:   { key: 'pending',   label: 'Landed · Bags Pending',  tone: 'info' },
  delivered: { key: 'delivered', label: 'Bags Delivered',         tone: 'success' },
  delayed:   { key: 'delayed',   label: 'Disembarkation Delayed', tone: 'danger' },
  resumed:   { key: 'resumed',   label: 'Disembarkation Resumed', tone: 'success' },
};

const STATUS_TABS = ['all', 'awaiting', 'pending', 'delivered', 'delayed', 'resumed'];

function deriveStatus(a) {
  if (a.disembark_resumed_announced) return STATUS_CONFIG.resumed;
  if (a.disembark_delay_announced) return STATUS_CONFIG.delayed;
  if (!a.touchdown_at) return STATUS_CONFIG.awaiting;
  if (!a.first_bag_at) return STATUS_CONFIG.pending;
  return STATUS_CONFIG.delivered;
}

const TERMINAL_COLORS = {
  T1: 'bg-blue-100 text-blue-700',
  T2: 'bg-purple-100 text-purple-700',
  T3: 'bg-orange-100 text-orange-700',
  T4: 'bg-pink-100 text-pink-700',
};

// ── Operator Cell ─────────────────────────────────────────────────────────────

function OperatorCell({ operators = [], small = false }) {
  const iconSize = small ? 11 : 13;
  const textCls = small ? 'text-xs text-gray-400' : 'text-xs';

  if (operators.length === 0) {
    return (
      <span className={`${textCls} text-orange-500 italic flex items-center gap-1`}>
        <UserCircle2 size={iconSize} className="text-orange-400" />
        No Operator
      </span>
    );
  }

  if (operators.length === 1) {
    return (
      <span className={`${textCls} text-gray-700 flex items-center gap-1`}>
        <UserCircle2 size={iconSize} className="text-gray-400 flex-shrink-0" />
        {operators[0].name}
      </span>
    );
  }

  return (
    <span className={`${textCls} text-gray-700 flex items-center gap-1`}>
      <UserCircle2 size={iconSize} className="text-gray-400 flex-shrink-0" />
      {operators[0].name}
      <span
        className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 font-semibold text-xs"
        title={operators.map(o => o.name).join(', ')}
      >
        +{operators.length - 1}
      </span>
    </span>
  );
}

// ── Assign Zone Operators Modal ───────────────────────────────────────────────

function AssignZoneOperatorsModal({ zone, onClose, onSaved }) {
  const [allOperators, setAllOperators] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [operatorSearch, setOperatorSearch] = useState('');

  useEffect(() => {
    async function load() {
      try {
        const [allRes, assignedRes] = await Promise.all([
          adminAPI.getAllZoneOperators(),
          adminAPI.getZoneOperators(zone.id),
        ]);
        setAllOperators(allRes.data.data || []);
        setSelectedIds((assignedRes.data.data || []).map(u => u.id));
      } catch {
        toast.error('Failed to load operators');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [zone.id]);

  async function handleSave() {
    setSaving(true);
    try {
      await adminAPI.setZoneOperators(zone.id, selectedIds);
      toast.success(`Operators updated for ${zone.zone_name}`);
      onSaved(zone.id, selectedIds);
      onClose();
    } catch {
      toast.error('Failed to save operator assignments');
    } finally {
      setSaving(false);
    }
  }

  const filtered = allOperators.filter(op =>
    !operatorSearch ||
    op.name.toLowerCase().includes(operatorSearch.toLowerCase()) ||
    op.email.toLowerCase().includes(operatorSearch.toLowerCase())
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-base font-bold text-gray-900">Assign Operators</h2>
            <p className="text-xs text-gray-500 mt-0.5">{zone.zone_name}</p>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
            <X size={16} />
          </button>
        </div>

        <div className="px-5 py-4">
          {loading ? (
            <div className="flex justify-center py-6">
              <Loader2 size={24} className="animate-spin text-blue-500" />
            </div>
          ) : allOperators.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">No zone operators found. Create one first.</p>
          ) : (
            <>
              <div className="relative mb-3">
                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search operators..."
                  value={operatorSearch}
                  onChange={e => setOperatorSearch(e.target.value)}
                  className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div className="max-h-52 overflow-y-auto border border-gray-100 rounded-xl divide-y divide-gray-50">
                {filtered.map(op => {
                  const checked = selectedIds.includes(op.id);
                  return (
                    <label
                      key={op.id}
                      className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 cursor-pointer"
                    >
                      <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${
                        checked ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                      }`}>
                        {checked && <Check size={10} className="text-white" />}
                      </div>
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={checked}
                        onChange={e => {
                          setSelectedIds(prev =>
                            e.target.checked ? [...prev, op.id] : prev.filter(id => id !== op.id)
                          );
                        }}
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{op.name}</p>
                        <p className="text-xs text-gray-400 truncate">{op.email}</p>
                      </div>
                    </label>
                  );
                })}
                {filtered.length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-3">No match for "{operatorSearch}"</p>
                )}
              </div>

              <p className="text-xs text-gray-400 mt-2">
                {selectedIds.length === 0
                  ? 'No operators assigned'
                  : `${selectedIds.length} operator${selectedIds.length > 1 ? 's' : ''} selected`}
              </p>
            </>
          )}
        </div>

        <div className="flex gap-3 px-5 pb-5">
          <Button variant="ghost" onClick={onClose} size="lg" className="flex-1">
            Cancel
          </Button>
          <Button variant="primary" loading={saving} disabled={loading} onClick={handleSave} size="lg" className="flex-1">
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function BeltAssignmentsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  // Reused under both /admin and /supervisor — link to whichever shell's
  // pages the row/detail navigation should land on.
  const announcementsBasePath = location.pathname.startsWith('/admin')
    ? '/admin/airport-announcements'
    : '/supervisor/announcements';
  const detailBasePath = location.pathname.startsWith('/admin')
    ? '/admin/belt-assignments'
    : '/supervisor/belts';
  const zoneQueueBasePath = location.pathname.startsWith('/admin')
    ? '/admin/belt-assignments/zone'
    : '/supervisor/belts/zone';

  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [scope, setScope] = useState('active'); // 'active' | 'all'
  const [statusTab, setStatusTab] = useState('all');
  const [terminalFilter, setTerminalFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [lastLoadedAt, setLastLoadedAt] = useState(null);
  const [zonesByCode, setZonesByCode] = useState({});
  const [zoneOperators, setZoneOperators] = useState({}); // zone_code -> operators[]
  const [assignModal, setAssignModal] = useState(null); // zone object

  const load = useCallback(async (isManualRefresh = false) => {
    if (isManualRefresh) setRefreshing(true);
    try {
      const res = scope === 'active'
        ? await beltAssignmentsAPI.getActive()
        : await beltAssignmentsAPI.getAll({ limit: 200 });
      setAssignments(res.data.data || []);
      setLastLoadedAt(Date.now());
    } catch {
      toast.error('Failed to load belt assignments');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [scope]);

  useEffect(() => {
    setLoading(true);
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [load]);

  // Counts for the status tab badges (over the currently loaded scope, pre-filter)
  const counts = useMemo(() => {
    const acc = {};
    for (const a of assignments) {
      const s = deriveStatus(a).key;
      acc[s] = (acc[s] || 0) + 1;
    }
    return acc;
  }, [assignments]);

  const terminals = useMemo(
    () => [...new Set(assignments.map(a => a.terminal).filter(Boolean))].sort(),
    [assignments]
  );

  // Load zones once (id + zone_code needed to fetch/save operator assignments,
  // since BeltAssignment.terminal only stores the zone_code string).
  useEffect(() => {
    announcementZonesAPI.getAll()
      .then(res => {
        const byCode = {};
        for (const z of res.data.data || []) byCode[z.zone_code] = z;
        setZonesByCode(byCode);
      })
      .catch(() => {});
  }, []);

  const loadZoneOperators = useCallback(async (zoneCode) => {
    const zone = zonesByCode[zoneCode];
    if (!zone) return;
    try {
      const res = await adminAPI.getZoneOperators(zone.id);
      setZoneOperators(prev => ({ ...prev, [zoneCode]: res.data.data || [] }));
    } catch { /* non-fatal — operator cell just shows "No Operator" */ }
  }, [zonesByCode]);

  // Fetch operators for every terminal currently on the board (small fixed set).
  useEffect(() => {
    if (Object.keys(zonesByCode).length === 0) return;
    terminals.forEach(t => loadZoneOperators(t));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [terminals, zonesByCode]);

  const filtered = useMemo(() => {
    let list = assignments;
    if (statusTab !== 'all') list = list.filter(a => deriveStatus(a).key === statusTab);
    if (terminalFilter !== 'all') list = list.filter(a => a.terminal === terminalFilter);
    if (search.trim()) {
      const q = search.trim().toUpperCase();
      list = list.filter(a => a.flight_number.includes(q) || (a.airline || '').toUpperCase().includes(q));
    }
    return [...list].sort((a, b) => new Date(a.scheduled_arrival_time) - new Date(b.scheduled_arrival_time));
  }, [assignments, statusTab, terminalFilter, search]);

  function openAnnouncement(id, e) {
    e?.stopPropagation();
    if (!id) return;
    navigate(`${announcementsBasePath}?id=${id}`);
  }

  function openDetail(assignmentId) {
    navigate(`${detailBasePath}/${assignmentId}`);
  }

  function openZoneDisplay(terminal, e) {
    e?.stopPropagation();
    if (!terminal) return;
    window.open(`/zone-display/${terminal}`, '_blank', 'noopener,noreferrer');
  }

  function openZoneQueue(terminal, flightNumber, e) {
    e?.stopPropagation();
    if (!terminal) return;
    const query = flightNumber ? `?flight=${encodeURIComponent(flightNumber)}` : '';
    navigate(`${zoneQueueBasePath}/${terminal}/queue${query}`);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-4 border-blue-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="px-4 py-5 max-w-7xl mx-auto">
      {/* Assign Zone Operators Modal */}
      {assignModal && (
        <AssignZoneOperatorsModal
          zone={assignModal}
          onClose={() => setAssignModal(null)}
          onSaved={() => loadZoneOperators(assignModal.zone_code)}
        />
      )}

      {/* Header */}
      <PageHeader
        icon={Luggage}
        title="Belt Assignments"
        subtitle={`Arrivals — baggage belts across every terminal${lastLoadedAt ? ` · refreshed ${timeAgo(new Date(lastLoadedAt).toISOString())}` : ''}`}
        actions={
          <>
            <div className="flex gap-1 bg-white border border-gray-200 rounded-lg p-1">
              {['active', 'all'].map(s => (
                <button
                  key={s}
                  onClick={() => { setScope(s); setLoading(true); }}
                  className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors ${
                    scope === s ? 'bg-blue-700 text-white' : 'text-gray-500 hover:bg-gray-100'
                  }`}
                >
                  {s === 'active' ? 'Active' : 'All (last 200)'}
                </button>
              ))}
            </div>
            <Button variant="ghost" size="sm" icon={RefreshCw} loading={refreshing} onClick={() => load(true)}>
              Refresh
            </Button>
          </>
        }
      />

      {/* Status tabs */}
      <div className="flex gap-1 mt-4 mb-3 overflow-x-auto pb-1">
        {STATUS_TABS.map(tab => {
          const count = tab === 'all' ? assignments.length : (counts[tab] || 0);
          const active = statusTab === tab;
          const cfg = STATUS_CONFIG[tab];
          return (
            <button
              key={tab}
              onClick={() => setStatusTab(tab)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                active ? 'bg-blue-700 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {tab === 'all' ? 'All' : cfg.label}
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${
                active ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'
              }`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Filters row */}
      <div className="flex gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search flight or airline…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          />
        </div>
        <div className="flex items-center gap-1">
          <Filter size={13} className="text-gray-400" />
          <div className="flex gap-1">
            <button
              onClick={() => setTerminalFilter('all')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                terminalFilter === 'all' ? 'bg-gray-800 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              All terminals
            </button>
            {terminals.map(t => (
              <button
                key={t}
                onClick={() => setTerminalFilter(terminalFilter === t ? 'all' : t)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  terminalFilter === t ? 'bg-gray-800 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      </div>

      <p className="text-xs text-gray-400 mb-2">
        Showing {filtered.length} of {assignments.length} assignment{assignments.length !== 1 ? 's' : ''}
      </p>

      {/* Table */}
      {filtered.length === 0 ? (
        <EmptyState icon={Radio} title="No belt assignments found" subtitle="Try adjusting the filters" />
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-xs text-gray-500 uppercase tracking-wide">
                  <th className="text-left px-4 py-3 font-semibold">Terminal / Belt</th>
                  <th className="text-left px-4 py-3 font-semibold">Flight</th>
                  <th className="text-left px-4 py-3 font-semibold">Scheduled</th>
                  <th className="text-left px-4 py-3 font-semibold">Touchdown</th>
                  <th className="text-left px-4 py-3 font-semibold">Language</th>
                  <th className="text-left px-4 py-3 font-semibold">Operator</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th className="text-left px-4 py-3 font-semibold">Source</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map(a => {
                  const status = deriveStatus(a);
                  const termColor = TERMINAL_COLORS[a.terminal] || 'bg-gray-100 text-gray-600';
                  return (
                    <tr
                      key={a.id}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => openDetail(a.id)}
                    >
                      {/* Terminal / Belt */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {a.terminal && (
                            <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${termColor}`}>
                              {a.terminal}
                            </span>
                          )}
                          <span className="text-gray-700">
                            Belt <span className="font-bold text-gray-900">{a.belt_number || '—'}</span>
                          </span>
                          {a.belt_change_count > 0 && (
                            <span className="text-xs bg-amber-50 text-amber-600 border border-amber-200 rounded-full px-2 py-0.5">
                              changed ×{a.belt_change_count}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Flight */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <PlaneLanding size={13} className="text-blue-500 flex-shrink-0" />
                          <span className="font-semibold text-gray-800">{a.flight_number}</span>
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5 ml-5">
                          {a.airline}{a.origin ? ` · from ${a.origin}` : ''}
                        </p>
                      </td>

                      {/* Scheduled */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 text-gray-700">
                          <Clock3 size={12} className="text-gray-400" />
                          {timeOfDay(a.scheduled_arrival_time)}
                        </div>
                      </td>

                      {/* Touchdown */}
                      <td className="px-4 py-3 text-gray-600">
                        {a.touchdown_at ? (
                          <span className="flex items-center gap-1.5">
                            <PackageCheck size={13} className="text-gray-300" />
                            {timeAgo(a.touchdown_at)}
                          </span>
                        ) : (
                          <span className="flex items-center gap-1.5 text-gray-300">
                            <Clock3 size={13} /> not yet
                          </span>
                        )}
                      </td>

                      {/* Language */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <Globe size={12} className="text-gray-400" />
                          {languagePairLabel(a.secondary_language)}
                          {a.language_auto_detected && <span className="text-emerald-500">auto</span>}
                        </div>
                      </td>

                      {/* Operator */}
                      <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center gap-1.5">
                          <OperatorCell operators={zoneOperators[a.terminal] || []} />
                          {zonesByCode[a.terminal] && (
                            <button
                              onClick={e => {
                                e.stopPropagation();
                                setAssignModal(zonesByCode[a.terminal]);
                              }}
                              title="Manage zone operators"
                              className="p-1 rounded-md text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors flex-shrink-0"
                            >
                              <UserCog size={13} />
                            </button>
                          )}
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3">
                        <StatusBadge label={status.label} tone={status.tone} dot />
                      </td>

                      {/* Source */}
                      <td className="px-4 py-3">
                        {a.auto_created ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-purple-700 bg-purple-50 px-2 py-0.5 rounded-full">
                            <Zap size={11} />
                            Auto
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">Manual</span>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 justify-end">
                          <button
                            onClick={e => openAnnouncement(a.last_announcement_id, e)}
                            title="View announcement"
                            disabled={!a.last_announcement_id}
                            className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-500 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                          >
                            <Volume2 size={14} />
                          </button>
                          <button
                            onClick={e => openZoneQueue(a.terminal, a.flight_number, e)}
                            title="Manage queue"
                            disabled={!a.terminal}
                            className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-500 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                          >
                            <ListChecks size={14} />
                          </button>
                          <button
                            onClick={e => openZoneDisplay(a.terminal, e)}
                            title="Open zone display"
                            disabled={!a.terminal}
                            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                          >
                            <Monitor size={14} />
                          </button>
                          <ChevronRight size={16} className="text-gray-300" />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-gray-100">
            {filtered.map(a => {
              const status = deriveStatus(a);
              const termColor = TERMINAL_COLORS[a.terminal] || 'bg-gray-100 text-gray-600';
              return (
                <div
                  key={a.id}
                  className="px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50"
                  onClick={() => openDetail(a.id)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      {a.terminal && (
                        <span className={`text-xs px-1.5 py-0.5 rounded font-semibold ${termColor}`}>{a.terminal}</span>
                      )}
                      <span className="font-bold text-gray-900">Belt {a.belt_number || '—'}</span>
                      <StatusBadge label={status.label} tone={status.tone} />
                    </div>
                    <div className="flex items-center gap-1.5 text-sm text-gray-700">
                      <PlaneLanding size={12} className="text-blue-500" />
                      <span className="font-semibold">{a.flight_number}</span>
                      {a.origin && <span className="text-gray-400">from {a.origin}</span>}
                    </div>
                    <div className="mt-0.5">
                      <OperatorCell operators={zoneOperators[a.terminal] || []} small />
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                      <Globe size={10} className="text-gray-300" />
                      {languagePairLabel(a.secondary_language)}
                      {a.language_auto_detected && <span className="text-emerald-500">auto</span>}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {timeOfDay(a.scheduled_arrival_time)}
                      {a.auto_created && ' · Auto'}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {zonesByCode[a.terminal] && (
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          setAssignModal(zonesByCode[a.terminal]);
                        }}
                        className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-500"
                        title="Manage operators"
                      >
                        <UserCog size={14} />
                      </button>
                    )}
                    <button
                      onClick={e => openAnnouncement(a.last_announcement_id, e)}
                      className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-500"
                      title="View announcement"
                    >
                      <Volume2 size={14} />
                    </button>
                    <button
                      onClick={e => openZoneQueue(a.terminal, a.flight_number, e)}
                      className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-500"
                      title="Manage queue"
                    >
                      <ListChecks size={14} />
                    </button>
                    <button
                      onClick={e => openZoneDisplay(a.terminal, e)}
                      className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"
                      title="Open zone display"
                    >
                      <Monitor size={14} />
                    </button>
                    <ChevronRight size={16} className="text-gray-300" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <p className="text-xs text-gray-400 mt-3 px-1">
        Belt assignments are created and updated automatically from FlightMS on each arrivals sync.
        {' '}<Monitor size={11} className="inline -mt-0.5" /> opens the live PA display for that terminal's zone.
      </p>
    </div>
  );
}
